import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';
import { XIcon } from '@phosphor-icons/react';
import { FullAlertRule, FullSource, SourceType } from '@/types';
import { CameraThumbnail, Thumbnail } from '@/components';
import { stripFileExtension } from '@/utils/file-name';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService, ThumbnailSize } from '@/services/engine';
import { AppRoutes } from '@/constants/routes';

interface SourcesPopoverProps {
    alertRule: FullAlertRule;
    onClose: () => void;
}

interface SourceItemProps {
    source: Pick<FullSource, 'id' | 'name' | 'src' | 'type'> &
        Partial<Pick<FullSource, 'displayName'>>;
    showDivider: boolean;
}

function SourceItem({ source, showDivider }: SourceItemProps) {
    const thumbnailUrl = EngineService.getSourceThumbnailUrl(
        source.id,
        ThumbnailSize.MQ,
    );
    const displayName = source.displayName ?? stripFileExtension(source.name);
    const isCamera = source.type === SourceType.CAMERA;

    return (
        <div>
            <Link
                to={generatePath(AppRoutes.SOURCE, { sourceId: source.id })}
                className="flex items-center gap-2 rounded p-1 transition-colors hover:bg-grey-600"
            >
                <div className="h-[37px] w-[66px] shrink-0 overflow-hidden rounded bg-grey-600">
                    {isCamera ? (
                        <CameraThumbnail
                            sourceId={source.id}
                            alt={displayName}
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <Thumbnail
                            imageSrc={thumbnailUrl}
                            size="fill"
                            imageUsesAuth
                        />
                    )}
                </div>
                <span className="min-w-0 flex-1 truncate text-sm text-grey-200">
                    {displayName}
                </span>
            </Link>
            {showDivider && <div className="my-2 h-px bg-grey-500" />}
        </div>
    );
}

export function SourcesPopover({ alertRule, onClose }: SourcesPopoverProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);

    const displaySources = alertRule.sources || [];

    return (
        <div className="flex w-[310px] flex-col gap-4 rounded-lg border border-grey-500 bg-grey-700 p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between">
                <h3 className="text-sm-medium text-white-100">
                    {t('infoPanel.sources')}
                </h3>
                <button
                    onClick={onClose}
                    className="flex h-4 w-4 items-center justify-center text-grey-300 transition-colors hover:text-white-100"
                >
                    <XIcon className="h-4 w-4" />
                </button>
            </div>

            <div className="-m-1 flex max-h-[288px] flex-col overflow-y-auto scrollbar-grey-600">
                {displaySources.map((source, index) => (
                    <SourceItem
                        key={source.id}
                        source={source}
                        showDivider={index < displaySources.length - 1}
                    />
                ))}
            </div>
        </div>
    );
}
